<template>
  <div class="invoice-detail-report">
    <v-card flat outlined class="report-table-card">
      <v-card-title class="report-table-header">
        <div>
          <div class="report-title">Invoice Details</div>
          <div class="report-subtitle">
            {{ InvoiceDetail.length }} invoice details found
          </div>
        </div>

        <v-spacer />

        <div class="summary-box">
          <div class="summary-label">Qty</div>
          <div class="summary-value">{{ totalQty }}</div>
        </div>

        <div class="summary-box">
          <div class="summary-label">Sales</div>
          <div class="summary-value">{{ totalSale | currency }}</div>
        </div>

        <div v-if="!hideCost" class="summary-box">
          <div class="summary-label">Cost</div>
          <div class="summary-value">{{ totalCost | currency }}</div>
        </div>

        <div v-if="!hideProfit" class="summary-box">
          <div class="summary-label">Profit</div>
          <div class="summary-value" :class="{ loss: totalProfit < 0 }">
            {{ totalProfit | currency }}
          </div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-progress-linear v-if="isLoading" indeterminate height="3" />

        <v-data-table
          v-else
          dense
          readonly
          :sort-by="sortBy"
          :mobile-breakpoint="0"
          :headers="visibleHeaders"
          :items="InvoiceDetail"
          :items-per-page="-1"
          hide-default-footer
          class="invoice-detail-table"
        >
          <template v-slot:item.invoice="{ item }">
            <a
              href="#"
              class="invoice-link"
              @click.prevent="viewInvoice(item.InvoiceId)"
            >
              {{ item.Invoice ? item.Invoice.localId : item.InvoiceId }}
            </a>
          </template>

          <template v-slot:item.customer="{ item }">
            <div class="customer-name">
              {{ getCustomerName(item) }}
            </div>

            <div
              v-if="item.Invoice && item.Invoice.Customer"
              class="customer-contact"
            >
              <v-chip
                v-if="item.Invoice.Customer.phoneNumber"
                x-small
                outlined
                class="mr-1"
              >
                <v-icon x-small left>mdi-phone</v-icon>
                {{ item.Invoice.Customer.phoneNumber }}
              </v-chip>

              <v-chip
                v-if="item.Invoice.Customer.cellNumber"
                x-small
                outlined
                color="primary"
              >
                <v-icon x-small left>mdi-cellphone-basic</v-icon>
                {{ item.Invoice.Customer.cellNumber }}
              </v-chip>
            </div>
          </template>

          <template v-slot:item.date="{ item }">
            <div>
              {{ getInvoiceDate(item) | moment("MM/DD/YYYY") }}
            </div>

            <div v-if="item.imeiNumber" class="imei-text">
              {{ item.imeiNumber }}
            </div>
          </template>

          <template v-slot:item.qty="{ item }">
            <span class="font-weight-bold">
              {{ item.qty }}
            </span>
          </template>

          <template v-slot:item.totalSale="{ item }">
            <span class="money">
              {{ item.totalSale | currency }}
            </span>
          </template>

          <template v-slot:item.totalCost="{ item }">
            <span class="money">
              {{ item.totalCost | currency }}
            </span>
          </template>

          <template v-slot:item.totalProfit="{ item }">
            <span class="money" :class="{ loss: getProfit(item) < 0 }">
              {{ getProfit(item) | currency }}
            </span>
          </template>

          <template v-slot:item.user="{ item }">
            {{ getUserName(item) }}
          </template>

          <template v-slot:body.append v-if="InvoiceDetail.length > 0">
            <tr class="total-row">
              <td colspan="3" class="text-right font-weight-bold">
                Total
              </td>

              <td class="text-center font-weight-bold">
                {{ totalQty }}
              </td>

              <td class="text-right font-weight-bold">
                {{ totalSale | currency }}
              </td>

              <td v-if="!hideCost" class="text-right font-weight-bold">
                {{ totalCost | currency }}
              </td>

              <td v-if="!hideProfit" class="text-right font-weight-bold">
                {{ totalProfit | currency }}
              </td>

              <td></td>
            </tr>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              No invoice details found for this product.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import invoiceDetail from "@/modules/Invoice/service.js";
import Hashids from "hashids";

const hashids = new Hashids();

export default {
  name: "invoice-detail-report",

  props: ["ProductId", "CustomerId", "hideProfit", "hideCost", "start", "end"],

  data() {
    return {
      isLoading: true,
      InvoiceDetail: [],
      sortBy: "invoice",
      headers: [
        {
          text: "Invoice #",
          align: "left",
          value: "invoice",
          sortable: true,
        },
        {
          text: "Customer",
          align: "left",
          value: "customer",
          sortable: true,
        },
        {
          text: "Date / IMEI",
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Qty",
          align: "center",
          value: "qty",
          sortable: true,
        },
        {
          text: "Price",
          align: "right",
          value: "totalSale",
          sortable: true,
        },
        {
          text: "Cost",
          align: "right",
          value: "totalCost",
          sortable: true,
        },
        {
          text: "Profit",
          align: "right",
          value: "totalProfit",
          sortable: true,
        },
        {
          text: "User",
          align: "left",
          value: "user",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    visibleHeaders() {
      return this.headers.filter((header) => {
        if (this.hideCost && header.value === "totalCost") return false;
        if (this.hideProfit && header.value === "totalProfit") return false;

        return true;
      });
    },

    totalProfit() {
      return this.totalSale - this.totalCost;
    },

    totalSale() {
      return this.InvoiceDetail.reduce(
        (sum, row) => sum + Number(row.totalSale || 0),
        0
      );
    },

    totalCost() {
      return this.InvoiceDetail.reduce(
        (sum, row) => sum + Number(row.totalCost || 0),
        0
      );
    },

    totalQty() {
      return this.InvoiceDetail.reduce(
        (sum, row) => sum + Number(row.qty || 0),
        0
      );
    },
  },

  watch: {
    CustomerId() {
      this.getInvoiceDetail();
    },
    ProductId() {
      this.getInvoiceDetail();
    },
    start() {
      this.getInvoiceDetail();
    },
    end() {
      this.getInvoiceDetail();
    },
  },

  async created() {
    await this.getInvoiceDetail();
  },

  methods: {
    getInvoiceDate(item) {
      return item.Invoice ? item.Invoice.date : null;
    },

    getCustomerName(item) {
      if (item.Invoice && item.Invoice.Customer && item.Invoice.Customer.name) {
        return item.Invoice.Customer.name;
      }

      return "Unknown Customer";
    },

    getUserName(item) {
      if (item.Invoice && item.Invoice.User && item.Invoice.User.name) {
        return item.Invoice.User.name;
      }

      return "N/A";
    },

    getProfit(item) {
      return Number(item.totalSale || 0) - Number(item.totalCost || 0);
    },

    viewInvoice(invoice) {
      const hashId = hashids.encode(invoice);

      const routeData = this.$router.resolve({
        path: this.$route.meta.public
          ? `/public/invoice/${hashId}/view`
          : `/global/invoice/${hashId}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850,height=1100");
    },

    async getInvoiceDetail() {
      this.isLoading = true;

      const where = {};

      if (this.start) where.start = this.start;
      if (this.end) where.end = this.end;
      if (this.CustomerId) where.CustomerId = this.CustomerId;
      if (this.ProductId) where.ProductId = this.ProductId;

      try {
        const response = await invoiceDetail.getAllInvoiceDetail(where);
        this.InvoiceDetail = response.data || [];
      } catch (error) {
        console.error("getInvoiceDetail failed", error);
        this.InvoiceDetail = [];
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.invoice-detail-report {
  width: 100%;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 110px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.summary-value.loss,
.money.loss {
  color: #b91c1c;
}

.invoice-link {
  font-weight: 700;
  text-decoration: none;
}

.customer-name {
  font-weight: 600;
  text-transform: capitalize;
  color: #111827;
}

.customer-contact {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.imei-text {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.money {
  font-weight: 700;
}

.total-row {
  background: #f9fafb;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.invoice-detail-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.invoice-detail-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .report-table-card {
    border: none !important;
    border-radius: 0;
  }

  .report-table-header {
    padding: 10px 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }
}
</style>